import { createContext, useContext, useEffect, useState } from "react";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";
import {
  doc, getDoc, setDoc, serverTimestamp,
  collection, query, where, getDocs, writeBatch,
} from "firebase/firestore";
import { auth, db } from "./firebase";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  /** Creates the Firestore profile for a first-time sign-in. If an admin
   *  previously created a manual customer record for this same phone number
   *  (via Admin > Add Customer — which has no way to know the real auth UID
   *  up front), that record's name/role are adopted here, and any bookings/
   *  subscriptions/cars it owns are copied over to the real UID.
   *
   *  Carrying those docs over is done as a CREATE of a new doc already
   *  tagged with this uid, not an UPDATE of the old doc in place — owner-
   *  based security rules (the common "resource.data.uid == request.auth.uid"
   *  shape) evaluate an update against the doc's CURRENT owner, which is
   *  still the old placeholder id at that point, so the update gets denied.
   *  A create is evaluated against the INCOMING data instead, which already
   *  has the real uid, so it passes the same rule. Cleanup of the old docs
   *  (delete + mark the old user merged) is attempted afterwards as its own
   *  best-effort step — if it's denied, the customer already has working
   *  copies of everything, and admin's "Merge Duplicate Customers" button
   *  can tidy up the leftover old records later with elevated access.
   *
   *  All of this is kept OUT of the customer's own profile write (done
   *  first, unconditionally) so a denial anywhere in the merge can never
   *  block login itself. */
  const ensureUserProfile = async (fbUser) => {
    const ref = doc(db, "users", fbUser.uid);
    const snap = await getDoc(ref);
    if (snap.exists()) return { id: snap.id, ...snap.data() };

    let oldDoc = null;
    if (fbUser.phoneNumber) {
      try {
        const dupSnap = await getDocs(query(collection(db, "users"), where("phone", "==", fbUser.phoneNumber)));
        oldDoc = dupSnap.docs.find((d) => d.id !== fbUser.uid && !d.data().merged) || null;
      } catch (e) {
        console.warn("Duplicate-customer lookup failed:", e.message);
      }
    }
    const old = oldDoc?.data();

    const data = {
      phone: fbUser.phoneNumber || "",
      name: old?.name || fbUser.displayName || "",
      role: old?.role || "customer",
      createdAt: old?.createdAt || serverTimestamp(),
      ...(oldDoc ? { mergedFrom: oldDoc.id } : {}),
    };
    await setDoc(ref, data);

    if (oldDoc) {
      let ownedByCollection = {};
      try {
        const copyBatch = writeBatch(db);
        for (const colName of ["bookings", "subscriptions", "cars"]) {
          const ownedSnap = await getDocs(query(collection(db, colName), where("uid", "==", oldDoc.id)));
          ownedByCollection[colName] = ownedSnap.docs;
          ownedSnap.forEach((d) => {
            const newRef = doc(collection(db, colName));
            // copiedFromId lets admin's "Merge Duplicate Customers" recognize
            // this old doc as already carried over (if the cleanup delete
            // below fails) and delete the leftover instead of re-copying it.
            copyBatch.set(newRef, { ...d.data(), uid: fbUser.uid, copiedFromId: d.id });
          });
        }
        await copyBatch.commit();
      } catch (e) {
        // Profile already exists with the adopted name/role above, so the
        // customer isn't blocked — only the car/booking/subscription copy
        // didn't go through. Admin's "Merge Duplicate Customers" button can
        // finish this later with elevated access.
        console.warn("Copying prior manual records failed (profile still created):", e.message);
        ownedByCollection = {};
      }

      try {
        const cleanupBatch = writeBatch(db);
        for (const colName of Object.keys(ownedByCollection)) {
          ownedByCollection[colName].forEach((d) => cleanupBatch.delete(d.ref));
        }
        cleanupBatch.update(oldDoc.ref, { merged: true, mergedInto: fbUser.uid });
        await cleanupBatch.commit();
      } catch (e) {
        // Non-fatal: the new copies above already give the customer working
        // data. Leftover old docs are harmless duplicates admin can clear.
        console.warn("Cleanup of prior manual record failed (new copies already exist):", e.message);
      }
    }

    return { id: fbUser.uid, ...data };
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      try {
        if (u) {
          setProfile(await ensureUserProfile(u));
        } else {
          setProfile(null);
        }
      } catch (e) {
        // Login must never hang on a loading screen because profile setup
        // hit an unexpected error — fall back to a minimal local profile.
        console.error("Failed to load/create user profile:", e.message);
        setProfile(u ? { id: u.uid, phone: u.phoneNumber || "", name: u.displayName || "", role: "customer" } : null);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, []);

  /** Always create a fresh invisible reCAPTCHA verifier bound to a container ID.
   *  Firebase throws "reCAPTCHA has already been rendered in this element" if
   *  we reuse a verifier or leave the previous widget in the DOM, so we clear both. */
  const ensureRecaptcha = (containerId) => {
    if (window.recaptchaVerifier) {
      try { window.recaptchaVerifier.clear(); } catch {}
      window.recaptchaVerifier = null;
    }
    const el = document.getElementById(containerId);
    if (el) el.innerHTML = "";
    window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
      size: "invisible",
    });
    return window.recaptchaVerifier;
  };

  const sendOtp = async (phoneNumber, containerId) => {
    const verifier = ensureRecaptcha(containerId);
    return await signInWithPhoneNumber(auth, phoneNumber, verifier);
  };

  const verifyOtp = async (confirmationResult, code) => {
    const cred = await confirmationResult.confirm(code);
    // Profile creation/merging is handled by onAuthStateChanged (fires right
    // after sign-in) via ensureUserProfile — avoids a race between two
    // separate "does the doc exist yet" checks creating duplicate writes.
    return cred.user;
  };

  const updateName = async (name) => {
    if (!user) return;
    await updateProfile(user, { displayName: name });
    await setDoc(doc(db, "users", user.uid), { name }, { merge: true });
    setProfile((p) => ({ ...(p || {}), name }));
  };

  const logout = async () => {
    if (window.recaptchaVerifier) {
      try { window.recaptchaVerifier.clear(); } catch {}
      window.recaptchaVerifier = null;
    }
    await signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, sendOtp, verifyOtp, logout, updateName }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
